/**
 * VehicleChangePanel — move a work order to another vehicle in place (#3285).
 *
 * Opens under the vehicle card from "Vaihda ajoneuvo". Reuses the create
 * wizard's vehicle search in vehicleOnly mode, so the orderer's own cars list
 * first. The server (PATCH /bookings/:id with vehicleId) relinks the orderer
 * to the new car, writes the change to the history and refuses the change
 * once the order is billed or while the customer holds an unanswered quote
 * link; those refusals come back as codes and are translated here.
 *
 * Kept out of VehicleInfoTab so the card itself needs no query client: the
 * mutation hook mounts only while the panel is open.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { PartyRef } from '@/components/parties/PartyPicker';
import { UnifiedCustomerVehicleSearch } from '@/components/workorders/create/UnifiedCustomerVehicleSearch';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/hooks/useNotify';
import type { VehicleListItem } from '@/hooks/useVehicles';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { describeApiError } from '@/lib/describeApiError';

interface VehicleChangePanelProps {
  workOrder: WorkOrderDetail;
  onClose: () => void;
}

export function VehicleChangePanel({ workOrder, onClose }: VehicleChangePanelProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateBooking = useUpdateBooking();
  const [inputValue, setInputValue] = useState('');
  const [pendingVehicle, setPendingVehicle] = useState<VehicleListItem | null>(null);

  const ordererParty = useMemo((): PartyRef | null => {
    const orderer = workOrder.parties?.find((party) => party.role === 'orderer');
    if (!orderer) return null;
    return {
      partyType: orderer.partyType,
      personId: orderer.personId,
      companyId: orderer.companyId,
      name: orderer.name,
      phone: orderer.phone,
    };
  }, [workOrder.parties]);

  const handleSelect = (vehicle: VehicleListItem) => {
    if (vehicle.id === workOrder.vehicle.id) {
      onClose();
      return;
    }
    setPendingVehicle(vehicle);
  };

  const handleConfirm = async () => {
    if (!pendingVehicle) return;
    try {
      await updateBooking.mutateAsync({ id: workOrder.id, data: { vehicleId: pendingVehicle.id } });
      notify.success(
        t('vehicleTab.vehicleChanged'),
        t('vehicleTab.vehicleChangedDesc', {
          from: workOrder.vehicle.registrationNumber,
          to: pendingVehicle.registrationNumber,
        }),
      );
      setPendingVehicle(null);
      onClose();
    } catch (error) {
      notify.error(
        tc('error'),
        describeApiError(error, tc, {
          codePrefix: 'workorders:vehicleTab.error',
          fallback: t('editFailed'),
        }),
      );
      setPendingVehicle(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('vehicleTab.changeVehicleHint')}
      </Typography>
      <UnifiedCustomerVehicleSearch
        mode="vehicleOnly"
        inputValue={inputValue}
        onInputChange={setInputValue}
        ordererParty={ordererParty}
        onVehicleSelect={handleSelect}
        onPersonSelect={() => undefined}
        onCompanySelect={() => undefined}
        onCreateVehicle={() => {
          notify.info(t('vehicleTab.createVehicleFirst'));
        }}
        onCreatePerson={() => undefined}
        onCreateCompany={() => undefined}
        onLookupResolved={() => undefined}
        autoFocus
        autoSelectSoleVehicle={false}
      />
      <Button size="small" onClick={onClose} sx={{ alignSelf: 'flex-start' }}>
        {tc('cancel')}
      </Button>
      <ConfirmDialog
        open={pendingVehicle !== null}
        title={t('vehicleTab.confirmTitle')}
        message={t('vehicleTab.confirmMessage', {
          from: workOrder.vehicle.registrationNumber,
          to: pendingVehicle?.registrationNumber ?? '',
        })}
        confirmText={t('vehicleTab.changeVehicle')}
        confirmColor="warning"
        onConfirm={() => {
          void handleConfirm();
        }}
        onCancel={() => setPendingVehicle(null)}
        loading={updateBooking.isPending}
      />
    </Box>
  );
}
